import React, { useState, useEffect } from 'react';
import axios from 'axios';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const templateComponentsApi = axios.create({ baseURL: API_URL });

const AmplifierComponentTreeView = ({ amplifierId, onClose }) => {
  const [templateStructure, setTemplateStructure] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTemplateStructure = async () => {
      if (!amplifierId) return;
      setLoading(true);
      try {
        const response = await templateComponentsApi.get(`/amplifier_template_structure/${amplifierId}`);
        console.log('Template structure response:', response.data); // Debug log
        setTemplateStructure(response.data);
      } catch (error) {
        console.error('Error fetching template structure:', error);
        setTemplateStructure([]);
      }
      setLoading(false);
    };
    
    fetchTemplateStructure();
  }, [amplifierId]);

  // Group the data by sections
  const groupedBySections = templateStructure.reduce((acc, item) => {
    const sectionId = item.section_id;
    if (!acc[sectionId]) {
      acc[sectionId] = {
        section_id: item.section_id,
        section_name: item.section_name,
        section_order: item.section_order,
        part_types: []
      };
    }
    
    // Check if part type already exists in this section
    const existingPartType = acc[sectionId].part_types.find(pt => pt.part_type_id === item.part_type_id);
    if (!existingPartType) {
      acc[sectionId].part_types.push({
        part_type_id: item.part_type_id,
        part_type_name: item.part_type_name,
        part_type_order: item.part_type_order,
        assigned_components: Array.isArray(item.assigned_components) ? item.assigned_components : []
      });
    }
    
    return acc;
  }, {});

  const sections = Object.values(groupedBySections).sort((a, b) => a.section_order - b.section_order);

  console.log('Grouped sections:', sections); // Debug log

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading amplifier component structure...</p>
      </div>
    );
  }

  if (templateStructure.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No template structure found for this amplifier.</p>
        <button onClick={onClose} style={{ 
          marginTop: '1rem', 
          padding: '0.5rem 1rem', 
          backgroundColor: '#007bff', 
          color: 'white', 
          border: 'none', 
          borderRadius: '4px', 
          cursor: 'pointer',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        maxWidth: '80vw',
        maxHeight: '80vh',
        overflow: 'auto',
        minWidth: '600px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>Amplifier Component Structure</h3>
          <button 
            onClick={onClose} 
            style={{ 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              padding: '0.5rem 1rem', 
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
          >
            Close
          </button>
        </div>

        <div style={{ fontFamily: 'monospace', lineHeight: '1.6' }}>
          {sections.map(section => (
            <div key={section.section_id} style={{ marginBottom: '1.5rem' }}>
              <div style={{ 
                fontSize: '1.2em', 
                fontWeight: 'bold', 
                color: '#2c3e50',
                marginBottom: '0.5rem',
                borderBottom: '2px solid #3498db',
                paddingBottom: '0.25rem'
              }}>
                {section.section_name}
              </div>
              
              {section.part_types
                .sort((a, b) => a.part_type_order - b.part_type_order)
                .map(partType => {
                  const components = partType.assigned_components || [];
                  const hasComponents = components.length > 0;
                  
                  return (
                    <div key={partType.part_type_id} style={{ marginLeft: '1rem', marginBottom: '0.75rem' }}>
                      {hasComponents && components.length === 1 ? (
                        // Single component - display inline with part type
                        <div style={{ color: '#34495e' }}>
                          <span style={{ fontWeight: '600' }}>├── {partType.part_type_name}:</span>
                          <span style={{ marginLeft: '0.5rem', color: '#16a085' }}>
                            {components[0].component_name} ({components[0].manufacturer_name})
                            {components[0].quantity > 1 && ` × ${components[0].quantity}`}
                            {components[0].notes && ` - ${components[0].notes}`}
                          </span>
                        </div>
                      ) : (
                        // Multiple components or no components
                        <div>
                          <div style={{ fontWeight: '600', color: '#34495e' }}>
                            ├── {partType.part_type_name}
                            {!hasComponents && <span style={{ fontStyle: 'italic', color: '#7f8c8d', marginLeft: '0.5rem' }}>(no components assigned)</span>}
                          </div>
                          
                          {hasComponents && components.length > 1 && (
                            <div style={{ marginLeft: '1rem', marginTop: '0.25rem' }}>
                              {components.map((component, index) => (
                                <div key={component.amplifier_template_component_id} style={{ color: '#16a085', marginBottom: '0.25rem' }}>
                                  <span>• {component.component_name} ({component.manufacturer_name})</span>
                                  {component.quantity > 1 && <span> × {component.quantity}</span>}
                                  {component.notes && <span style={{ fontStyle: 'italic', color: '#7f8c8d' }}> - {component.notes}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AmplifierComponentTreeView;
